import { ComboContract, ProductContract } from 'models'
import { useCallback, useMemo, useState } from 'react'
import useBrandFilter from 'components/BrandFilter/useBrandFtiler'
import { TOption } from 'components/OWForm/OWSelect'
import { isMatchedProductByQueryText } from 'pages/ProductsPage/shared/productFilter'
import toast from 'utilities/toast/toast'
import { t } from 'i18next'
import { StatusOptions } from 'components/Selector/StatusSelector'

export enum AvailabilityOptions {
    All,
    Unavailable,
    Available,
}

export const availabilityOptions: TOption[] = [
    { label: 'All', value: AvailabilityOptions.All },
    { label: 'Available', value: AvailabilityOptions.Available },
    { label: 'Unavailable', value: AvailabilityOptions.Unavailable },
]

export function useBasicProductLookupFilter<
    T extends ProductContract | ComboContract
>({
    locationProductBrandNames,
    products,
}: {
    locationProductBrandNames: string[]
    products: T[]
}) {
    const {
        brandNumber,
        brandOptions,
        selectedBrand,
        setSelectedBrand,
        handleClearFilter: clearBrandFilter,
    } = useBrandFilter(locationProductBrandNames)

    const [filterCategory, setFilterCategory] = useState<string[]>([])
    const [filterStatus, setFilterStatus] = useState<StatusOptions>(
        StatusOptions.All,
    )
    const [filterAvailability, setFilterAvailability] = useState(
        AvailabilityOptions.All,
    )
    const [filterInventoryStatus, setFilterInventoryStatus] = useState(-1)
    const [queryText, setQueryText] = useState('')

    const filteredProducts = useMemo(() => {
        const brandFilter = (p: ProductContract): boolean => {
            if (selectedBrand.length !== 0) {
                return selectedBrand.some((b) => b === (p.brandName || ''))
            }
            return true
        }
        return products.filter((d) => {
            if (d.baseItemID) return false

            return (
                isMatchedProductByQueryText(d, queryText) &&
                (filterStatus === StatusOptions.All ||
                    (filterStatus === StatusOptions.Active && d.isActive) ||
                    (filterStatus === StatusOptions.Inactive && !d.isActive)) &&
                (filterCategory.length === 0 ||
                    filterCategory.includes(d.categoryID ?? '')) &&
                brandFilter(d)
            )
        })
    }, [products, selectedBrand, queryText, filterStatus, filterCategory])

    const handleBarcodeScanned = useCallback(
        (barcode: string) => {
            if (!barcode) return

            const filteredByBarcodeProducts = products.filter((d) => {
                if (d.baseItemID) return false

                return d.barcode?.toLowerCase().includes(barcode.toLowerCase())
            })

            if (!filteredByBarcodeProducts?.length) {
                toast.error(
                    t('products.error.item.barcode_not_found', {
                        barcode: barcode,
                    }),
                )
            }

            setQueryText(barcode)
        },
        [products],
    )

    const handleClearFilter = useCallback(() => {
        setQueryText('')
        setFilterCategory([])
        setFilterStatus(StatusOptions.All)
        setFilterAvailability(AvailabilityOptions.All)
        clearBrandFilter()
    }, [clearBrandFilter])

    return {
        basicFilteredProducts: filteredProducts,
        filterCategory,
        filterStatus,
        filterAvailability,
        queryText,
        brandNumber,
        brandOptions,
        selectedBrand,
        filterInventoryStatus,
        setFilterCategory,
        setFilterStatus,
        setFilterAvailability,
        setFilterInventoryStatus,
        setQueryText,
        setSelectedBrand,
        handleClearFilter,
        handleBarcodeScanned,
    }
}
